"use client";

import { ClerkLoaded, useClerk } from "@clerk/react";
import {
  type FC,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useSearchParams } from "react-router";

import { useNewAccountsOnlyNotice } from "~/feature/auth/hooks/useNewAccountsOnlyNotice";
import { useSignUpEvent } from "~/feature/auth/hooks/useSignUpEvent";
import { useSignUpUnsafeMetadata } from "~/feature/auth/hooks/useSignUpUnsafeMetadata";
import { type AuthCtaMode, AuthNotice } from "~/feature/auth/type";
import { markCampaignSignUpStarted } from "~/feature/auth/utils/campaignSignUpMark";
import { parseAuthMessage } from "~/feature/auth/utils/parseAuthMessage";
import {
  parseAuthParam,
  stripAuthParams,
} from "~/feature/auth/utils/parseAuthParam";
import { resolveAuthRedirectUrl } from "~/feature/auth/utils/resolveAuthRedirectUrl";
import { showAuthNotice } from "~/feature/auth/utils/showAuthNotice";

interface IFlushPendingAuthModalProps {
  pendingModeRef: RefObject<AuthCtaMode | null>;
  open: (mode: AuthCtaMode) => void;
}

const FlushPendingAuthModal: FC<IFlushPendingAuthModalProps> = ({
  pendingModeRef,
  open,
}) => {
  useEffect(() => {
    const mode = pendingModeRef.current;
    if (!mode) {
      return;
    }

    pendingModeRef.current = null;
    open(mode);
  }, [pendingModeRef, open]);

  return null;
};

export const AuthModalBridge: FC = () => {
  const clerk = useClerk();
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingModeRef = useRef<AuthCtaMode | null>(null);
  const paramHandledRef = useRef(false);
  const unsafeMetadata = useSignUpUnsafeMetadata();
  useNewAccountsOnlyNotice();
  useSignUpEvent();

  const openAuthModal = useCallback(
    (mode: AuthCtaMode) => {
      if (!clerk.loaded) {
        pendingModeRef.current = mode;
        return;
      }

      if (clerk.user) {
        showAuthNotice(AuthNotice.AlreadySignedIn);
        return;
      }

      const forceRedirectUrl = resolveAuthRedirectUrl(
        window.location.pathname,
        stripAuthParams(window.location.href)
      );
      if (mode === "sign-up") {
        if (unsafeMetadata?.signupCampaign) {
          markCampaignSignUpStarted();
        }
        clerk.openSignUp({ forceRedirectUrl, unsafeMetadata });
      } else {
        clerk.openSignIn({ forceRedirectUrl });
      }
    },
    [clerk, unsafeMetadata]
  );

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      const mode = parseAuthMessage(event.data);
      if (mode) {
        openAuthModal(mode);
      }
    };

    window.addEventListener("message", handleMessage);

    return () => window.removeEventListener("message", handleMessage);
  }, [openAuthModal]);

  useEffect(() => {
    const parsed = parseAuthParam(searchParams);
    if (!parsed || paramHandledRef.current) {
      return;
    }

    paramHandledRef.current = true;
    openAuthModal(parsed.mode);
    setSearchParams(parsed.cleanedParams, {
      replace: true,
      preventScrollReset: true,
    });
  }, [searchParams, setSearchParams, openAuthModal]);

  return (
    <ClerkLoaded>
      <FlushPendingAuthModal
        pendingModeRef={pendingModeRef}
        open={openAuthModal}
      />
    </ClerkLoaded>
  );
};
